import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiTimeLine } from '@remixicon/react'
import type { SurveyListItem, Task, TimelineEvent, Workspace, WorkspaceDetailResponse } from 'shared'
import { EmptyState, PageHeader, SectionSpinner } from '../../components/ui'
import Timeline from '../../components/Timeline'
import WhatIsYours from '../../components/member/WhatIsYours'
import type { TimelineEvent as TimelineEventType } from 'shared'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

/** Where a timeline entry leads on the member side. */
function hrefForEvent(event: TimelineEventType): string | null {
  if (!event.ref_id) return null

  switch (event.ref_type) {
    case 'survey':
      return `surveys/${event.ref_id}`
    case 'artifact':
      return `reports/${event.ref_id}`
    case 'task':
      return 'tasks'
    default:
      return null
  }
}

export default function MemberWorkspace() {
  const { t } = useTranslation()
  const { workspaceId } = useParams<{ workspaceId: string }>()

  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [surveys, setSurveys] = useState<SurveyListItem[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!workspaceId) return

    setLoading(true)
    Promise.all([
      api
        .get<WorkspaceDetailResponse>(`/api/workspaces/${workspaceId}`)
        .then((response) => setWorkspace(response.data)),
      api
        .get<{ data: TimelineEvent[] }>(`/api/workspaces/${workspaceId}/timeline`)
        .then((response) => setEvents(response.data)),
      // The open work, for the section above the timeline. Both listings
      // already hide drafts from a member, so what comes back is what they
      // are allowed to see.
      api
        .get<{ data: SurveyListItem[] }>(`/api/workspaces/${workspaceId}/surveys`)
        .then((response) => setSurveys(response.data)),
      api
        .get<{ data: Task[] }>(`/api/workspaces/${workspaceId}/tasks`)
        .then((response) => setTasks(response.data)),
    ])
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId])

  if (loading) return <SectionSpinner />

  if (!workspace) {
    return <EmptyState title={t('errors.notFound')} description={error ?? undefined} />
  }

  return (
    <>
      <PageHeader title={workspace.name} subtitle={workspace.description ?? undefined} />

      <WhatIsYours workspaceId={workspace.id} surveys={surveys} tasks={tasks} />

      <h2 className="mb-3 text-display-sm text-gray-950 dark:text-white">{t('timeline.title')}</h2>

      <section
        aria-label={t('timeline.title')}
        className="rounded-lg bg-white p-6 shadow-sm ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800"
      >
        {events.length === 0 ? (
          <EmptyState
            icon={<RiTimeLine className="size-6" aria-hidden="true" />}
            title={t('timeline.emptyTitle')}
            description={t('timeline.emptyMemberDescription')}
          />
        ) : (
          <Timeline events={events} hrefFor={hrefForEvent} />
        )}
      </section>
    </>
  )
}
